import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";

import { AuthApiError, resetPassword } from "../services/authApi";
import { getApiBase } from "../services/apiConfig";
import "../Components/admin/adminLogin.css";

function passwordMeetsRules(p) {
  if (!p || p.length < 8) return false;
  if (!/[a-z]/.test(p)) return false;
  if (!/[A-Z]/.test(p)) return false;
  if (!/\d/.test(p)) return false;
  return true;
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const tokenFromUrl = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [token, setToken] = useState(tokenFromUrl);
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (tokenFromUrl) setToken(tokenFromUrl);
  }, [tokenFromUrl]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!passwordMeetsRules(newPassword)) {
      setError("Password must be at least 8 characters and include uppercase, lowercase, and a digit.");
      return;
    }
    if (newPassword !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (!String(token).trim()) {
      setError("Reset token is missing. Open the link from your email.");
      return;
    }
    setSubmitting(true);
    try {
      if (!getApiBase()) {
        setError("API base URL is disabled. Set REACT_APP_API_BASE or restore the default.");
        return;
      }
      await resetPassword({ token: token.trim(), newPassword });
      setDone(true);
    } catch (err) {
      const msg = err instanceof AuthApiError ? err.message : err?.message || "Reset failed.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="adminLoginPage">
      <div className="adminLoginCard" role="dialog" aria-label="Reset password">
        <div className="adminLoginTop">
          <Link className="adminLoginBackHome" to="/admin/login">
            <span className="adminLoginBackHomeIcon" aria-hidden="true">
              <FaArrowLeft />
            </span>
            Admin sign in
          </Link>
          <h1 className="adminLoginTitle">Set new password</h1>
          <p className="adminLoginSubtitle">Use the token from your reset email. Minimum 8 characters, with upper, lower, and a number.</p>
        </div>

        {done ? (
          <div className="adminLoginForm" style={{ paddingTop: 0 }}>
            <div
              className="adminLoginFormError"
              style={{ color: "#047857", background: "rgba(16, 185, 129, 0.1)", borderColor: "rgba(16, 185, 129, 0.28)" }}
            >
              Password updated. You can sign in now.
            </div>
            <Link className="adminLoginButton" to="/admin/login" style={{ textAlign: "center", textDecoration: "none", display: "block" }}>
              Go to sign in
            </Link>
          </div>
        ) : (
          <form className="adminLoginForm" onSubmit={onSubmit}>
            {error ? (
              <div className="adminLoginFormError" role="alert">
                {error}
              </div>
            ) : null}
            <label className="adminLoginLabel" htmlFor="resetToken">
              Reset token
            </label>
            <div className="adminLoginField">
              <input
                id="resetToken"
                className="adminLoginInput"
                style={{ width: "100%" }}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                autoComplete="off"
                placeholder="Pasted from email link"
              />
            </div>
            <label className="adminLoginLabel" htmlFor="resetNew" style={{ marginTop: 10 }}>
              New password
            </label>
            <div className="adminLoginField">
              <input
                id="resetNew"
                className="adminLoginInput"
                style={{ width: "100%" }}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                type="password"
                autoComplete="new-password"
              />
            </div>
            <label className="adminLoginLabel" htmlFor="resetConfirm" style={{ marginTop: 10 }}>
              Confirm password
            </label>
            <div className="adminLoginField">
              <input
                id="resetConfirm"
                className="adminLoginInput"
                style={{ width: "100%" }}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                type="password"
                autoComplete="new-password"
              />
            </div>
            <button className="adminLoginButton" type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
